import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Card } from './ui/card';
import Button from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import ErrorBoundary from './ErrorBoundary';

interface AnalyticsData {
  interventions: InterventionStats[];
  checkInStats: CheckInStats;
  userEngagement: UserEngagementStats;
  performance: PerformanceStats;
}

interface InterventionStats {
  type: string;
  totalDelivered: number;
  responseRate: number;
  averageImpact: number;
  successRate: number;
}

interface CheckInStats {
  totalCheckIns: number;
  completionRate: number;
  currentStreak: number;
  longestStreak: number;
  averageDaily: number;
  weeklyPattern: { day: string; checkIns: number }[];
}

interface UserEngagementStats {
  totalSessions: number;
  averageSessionDuration: number;
  bounceRate: number;
  retentionRate: number;
  lastActive: string;
}

interface PerformanceStats {
  averageApiResponseTime: number;
  uptime: number;
  errorRate: number;
  cacheHitRate: number;
}

const AnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<'overview' | 'interventions' | 'checkins' | 'engagement' | 'performance'>('overview');

  const getUserIdFromToken = (): string | null => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const decoded: any = jwtDecode(token);
      return decoded.user?.id || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      const userId = getUserIdFromToken();
      if (!userId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };
        
        // Fetch all analytics data with full URL base path
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const [interventionsRes, checkInRes, engagementRes, performanceRes] = await Promise.all([
          axios.get<InterventionStats[]>(`${baseURL}/api/interventions/analytics`, config),
          axios.get<CheckInStats>(`${baseURL}/api/analytics/checkins`, config),
          axios.get<UserEngagementStats>(`${baseURL}/api/analytics/engagement`, config),
          axios.get<PerformanceStats>(`${baseURL}/api/analytics/performance`, config)
        ]);

        setAnalytics({
          interventions: interventionsRes.data,
          checkInStats: checkInRes.data,
          userEngagement: engagementRes.data,
          performance: performanceRes.data
        });
      } catch (err: any) {
        setError(err.response?.data?.msg || 'Failed to fetch analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatNumber = (value: number) => value.toFixed(1);
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div
          className="w-16 h-16 border-4 border-gray-300 border-t-gray-600 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-8 bg-white shadow-xl rounded-xl">
          <div className="text-text-primary text-center font-inter">
            {error}
          </div>
        </Card>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'Overview', icon: '📊' },
    { key: 'interventions', label: 'Interventions', icon: '🎯' },
    { key: 'checkins', label: 'Check-ins', icon: '✅' },
    { key: 'engagement', label: 'Engagement', icon: '🔥' },
    { key: 'performance', label: 'Performance', icon: '⚡' }
  ];

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Real-time Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-6 bg-white shadow-xl rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">📊</span>
              <span className="text-momentum-teal text-sm font-inter">Real-time</span>
            </div>
            <div className="text-2xl font-bold text-white font-inter">
              {analytics?.checkInStats.totalCheckIns || 0}
            </div>
            <div className="text-white/70 text-sm font-inter">Total Entries</div>
            <div className="text-xs text-momentum-teal mt-1 font-inter">Updated live</div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="p-6 bg-white shadow-xl rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">🔥</span>
              <span className="text-momentum-amber text-sm font-inter">Streak</span>
            </div>
            <div className="text-2xl font-bold text-white font-inter">
              {analytics?.checkInStats.currentStreak || 0}
            </div>
            <div className="text-white/70 text-sm font-inter">Days Active</div>
            <div className="text-xs text-momentum-emerald mt-1 font-inter">
              Best: {analytics?.checkInStats.longestStreak || 0}
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-6 bg-white shadow-xl rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">⚡</span>
              <span className="text-momentum-purple text-sm font-inter">Velocity</span>
            </div>
            <div className="text-2xl font-bold text-white font-inter">
              {formatNumber(analytics?.checkInStats.averageDaily || 0)}
            </div>
            <div className="text-white/70 text-sm font-inter">Entries/Day</div>
            <div className="text-xs text-momentum-teal mt-1 font-inter">7-day avg</div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="p-6 bg-white shadow-xl rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">📈</span>
              <span className="text-momentum-emerald text-sm font-inter">Growth</span>
            </div>
            <div className="text-2xl font-bold text-white font-inter">
              {formatPercentage(analytics?.checkInStats.completionRate || 0)}
            </div>
            <div className="text-white/70 text-sm font-inter">Completion Rate</div>
            <div className="text-xs text-momentum-emerald mt-1 font-inter">This month</div>
          </Card>
        </motion.div>
      </div>

      {/* Activity Heatmap */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-momentum-emerald font-inter">Weekly Activity Pattern</h3>
            <div className="text-xs text-white/70 font-inter">Last 4 weeks</div>
          </div>
          <div className="grid grid-cols-7 gap-3">
            {analytics?.checkInStats.weeklyPattern.map((day, index) => {
              const intensity = Math.min(day.checkIns / 7, 1);
              return (
                <div key={day.day} className="text-center">
                  <div className="text-white/70 text-sm font-inter mb-3">{day.day}</div>
                  <div 
                    className="rounded-lg mx-auto transition-all duration-500 hover:scale-110"
                    style={{ 
                      height: '60px',
                      width: '100%',
                      backgroundColor: `rgba(56, 178, 172, ${0.2 + (intensity * 0.8)})`,
                      border: `2px solid rgba(56, 178, 172, ${0.3 + (intensity * 0.7)})`
                    }}
                  />
                  <div className="text-white text-sm font-inter mt-2 font-medium">{day.checkIns}</div>
                  <div className="text-xs text-white/50 font-inter">
                    {intensity > 0.7 ? 'High' : intensity > 0.4 ? 'Med' : 'Low'}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </motion.div>

      {/* Momentum Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6 bg-white shadow-xl rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🎯</span>
              <h3 className="text-lg font-bold text-momentum-emerald font-inter">Smart Insights</h3>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-dark-glass rounded-lg">
                <div className="text-sm text-momentum-teal font-inter font-medium mb-1">Momentum Status</div>
                <div className="text-white/90 font-inter text-sm">
                  {(analytics?.checkInStats.currentStreak || 0) >= 7 
                    ? "🔥 You're on fire! Keep this momentum going."
                    : (analytics?.checkInStats.currentStreak || 0) >= 3
                    ? "📈 Building momentum. Stay consistent!"
                    : "🌱 Starting fresh. Focus on small daily wins."
                  }
                </div>
              </div>
              <div className="p-3 bg-dark-glass rounded-lg">
                <div className="text-sm text-momentum-amber font-inter font-medium mb-1">Engagement Pattern</div>
                <div className="text-white/90 font-inter text-sm">
                  {(analytics?.userEngagement.averageSessionDuration || 0) > 300
                    ? "⭐ Deep engagement detected. Quality over quantity!"
                    : "⚡ Quick check-ins work well for you. Stay efficient!"
                  }
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white shadow-xl rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🎯</span>
              <h3 className="text-lg font-bold text-momentum-emerald font-inter">Nudge Performance</h3>
            </div>
            <div className="space-y-4">
              {analytics?.interventions.slice(0, 3).map((intervention, index) => (
                <div key={intervention.type} className="flex items-center justify-between p-3 bg-dark-glass rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      {intervention.type === 'Celebration' ? '🎉' : 
                       intervention.type === 'AccountabilityCheck' ? '📋' :
                       intervention.type === 'GoalAdjustment' ? '🎯' : '🚀'}
                    </span>
                    <div>
                      <div className="text-white font-inter text-sm font-medium">
                        {intervention.type.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="text-white/60 font-inter text-xs">
                        {intervention.totalDelivered} sent
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-momentum-emerald font-inter text-sm font-medium">
                      {formatPercentage(intervention.responseRate)}
                    </div>
                    <div className="text-white/60 font-inter text-xs">response</div>
                  </div>
                </div>
              )) || (
                <div className="text-center py-8">
                  <div className="text-white/60 font-inter text-sm">No nudge data available</div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </motion.div>

      {/* Legacy Key Metrics for compatibility */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🎯</span>
            <span className="text-momentum-emerald text-sm font-inter">Interventions</span>
          </div>
          <div className="text-2xl font-bold text-white font-inter">
            {analytics?.interventions.reduce((sum, i) => sum + i.totalDelivered, 0) || 0}
          </div>
          <div className="text-white/70 text-sm font-inter">Total Delivered</div>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">✅</span>
            <span className="text-momentum-teal text-sm font-inter">Check-ins</span>
          </div>
          <div className="text-2xl font-bold text-white font-inter">
            {analytics?.checkInStats.currentStreak || 0}
          </div>
          <div className="text-white/70 text-sm font-inter">Current Streak</div>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🔥</span>
            <span className="text-momentum-amber text-sm font-inter">Engagement</span>
          </div>
          <div className="text-2xl font-bold text-white font-inter">
            {formatPercentage(analytics?.userEngagement.retentionRate || 0)}
          </div>
          <div className="text-white/70 text-sm font-inter">Retention Rate</div>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">⚡</span>
            <span className="text-momentum-purple text-sm font-inter">Performance</span>
          </div>
          <div className="text-2xl font-bold text-white font-inter">
            {analytics?.performance.averageApiResponseTime.toFixed(0) || 0}ms
          </div>
          <div className="text-white/70 text-sm font-inter">Avg Response</div>
        </Card>
      </motion.div>
    </div>
  );

  const renderInterventions = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {analytics?.interventions.map((intervention, index) => (
          <motion.div
            key={intervention.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="p-6 bg-white shadow-xl rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-momentum-emerald font-inter">
                  {intervention.type.replace(/([A-Z])/g, ' $1').trim()}
                </h3>
                <span className="text-2xl">
                  {intervention.type === 'Celebration' ? '🎉' : 
                   intervention.type === 'AccountabilityCheck' ? '📋' :
                   intervention.type === 'GoalAdjustment' ? '🎯' :
                   intervention.type === 'ReflectionPrompt' ? '🤔' : '🚀'}
                </span>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-white/70 font-inter">Delivered</span>
                  <span className="text-white font-inter font-medium">{intervention.totalDelivered}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70 font-inter">Response Rate</span>
                  <span className="text-momentum-teal font-inter font-medium">
                    {formatPercentage(intervention.responseRate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70 font-inter">Success Rate</span>
                  <span className="text-momentum-emerald font-inter font-medium">
                    {formatPercentage(intervention.successRate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70 font-inter">Avg Impact</span>
                  <span className="text-momentum-amber font-inter font-medium">
                    {formatNumber(intervention.averageImpact)}/10
                  </span>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderCheckIns = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="text-center">
            <div className="text-3xl font-bold text-momentum-teal font-inter mb-2">
              {analytics?.checkInStats.totalCheckIns || 0}
            </div>
            <div className="text-white/70 font-inter">Total Check-ins</div>
          </div>
        </Card>
        
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="text-center">
            <div className="text-3xl font-bold text-momentum-emerald font-inter mb-2">
              {formatPercentage(analytics?.checkInStats.completionRate || 0)}
            </div>
            <div className="text-white/70 font-inter">Completion Rate</div>
          </div>
        </Card>
        
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="text-center">
            <div className="text-3xl font-bold text-momentum-amber font-inter mb-2">
              {analytics?.checkInStats.currentStreak || 0}
            </div>
            <div className="text-white/70 font-inter">Current Streak</div>
          </div>
        </Card>
        
        <Card className="p-6 bg-white shadow-xl rounded-xl">
          <div className="text-center">
            <div className="text-3xl font-bold text-momentum-purple font-inter mb-2">
              {analytics?.checkInStats.longestStreak || 0}
            </div>
            <div className="text-white/70 font-inter">Longest Streak</div>
          </div>
        </Card>
      </div>

      {/* Weekly Pattern Chart */}
      <Card className="p-6 glass shadow-glass">
        <h3 className="text-xl font-bold text-momentum-emerald font-inter mb-4">Weekly Check-in Pattern</h3>
        <div className="grid grid-cols-7 gap-2">
          {analytics?.checkInStats.weeklyPattern.map((day, index) => (
            <div key={day.day} className="text-center">
              <div className="text-white/70 text-sm font-inter mb-2">{day.day}</div>
              <div 
                className="bg-momentum-teal rounded-lg mx-auto transition-all duration-300"
                style={{ 
                  height: `${Math.max(20, (day.checkIns / 7) * 60)}px`,
                  width: '100%'
                }}
              />
              <div className="text-white text-sm font-inter mt-2">{day.checkIns}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderEngagement = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-6 glass shadow-glass">
        <h3 className="text-xl font-bold text-momentum-emerald font-inter mb-4">User Engagement</h3>
        <div className="space-y-4">
          <div className="flex justify-between">
            <span className="text-white/70 font-inter">Total Sessions</span>
            <span className="text-white font-inter font-medium">
              {analytics?.userEngagement.totalSessions || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/70 font-inter">Avg Session Duration</span>
            <span className="text-momentum-teal font-inter font-medium">
              {formatDuration(analytics?.userEngagement.averageSessionDuration || 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/70 font-inter">Bounce Rate</span>
            <span className="text-momentum-amber font-inter font-medium">
              {formatPercentage(analytics?.userEngagement.bounceRate || 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/70 font-inter">Retention Rate</span>
            <span className="text-momentum-emerald font-inter font-medium">
              {formatPercentage(analytics?.userEngagement.retentionRate || 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/70 font-inter">Last Active</span>
            <span className="text-white font-inter font-medium">
              {analytics?.userEngagement.lastActive || 'N/A'}
            </span>
          </div>
        </div>
      </Card>

      <Card className="p-6 glass shadow-glass">
        <h3 className="text-xl font-bold text-momentum-emerald font-inter mb-4">Engagement Insights</h3>
        <div className="space-y-4">
          <div className="p-4 bg-dark-glass rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">💡</span>
              <span className="text-momentum-teal font-inter font-medium">Key Insight</span>
            </div>
            <p className="text-white/80 font-inter text-sm">
              {analytics?.userEngagement.retentionRate > 0.7 
                ? "Excellent user retention! Your momentum-building approach is working well."
                : analytics?.userEngagement.retentionRate > 0.5
                ? "Good engagement patterns. Consider optimizing intervention timing."
                : "Focus on improving early user experience and onboarding flow."
              }
            </p>
          </div>
          
          <div className="p-4 bg-dark-glass rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📈</span>
              <span className="text-momentum-amber font-inter font-medium">Recommendation</span>
            </div>
            <p className="text-white/80 font-inter text-sm">
              {analytics?.userEngagement.averageSessionDuration > 300
                ? "Users are highly engaged. Consider adding more advanced features."
                : "Short session durations suggest need for more compelling content."
              }
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderPerformance = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card className="p-6 glass shadow-glass">
        <div className="text-center">
          <div className="text-3xl font-bold text-momentum-teal font-inter mb-2">
            {analytics?.performance.averageApiResponseTime.toFixed(0) || 0}ms
          </div>
          <div className="text-white/70 font-inter">Avg API Response</div>
          <div className={`text-sm font-inter mt-2 ${
            (analytics?.performance.averageApiResponseTime || 0) < 500 ? 'text-momentum-emerald' : 
            (analytics?.performance.averageApiResponseTime || 0) < 1000 ? 'text-momentum-amber' : 'text-red-400'
          }`}>
            {(analytics?.performance.averageApiResponseTime || 0) < 500 ? 'Excellent' : 
             (analytics?.performance.averageApiResponseTime || 0) < 1000 ? 'Good' : 'Needs Improvement'}
          </div>
        </div>
      </Card>
      
      <Card className="p-6 glass shadow-glass">
        <div className="text-center">
          <div className="text-3xl font-bold text-momentum-emerald font-inter mb-2">
            {formatPercentage(analytics?.performance.uptime || 0)}
          </div>
          <div className="text-white/70 font-inter">Uptime</div>
          <div className="text-momentum-emerald text-sm font-inter mt-2">
            {(analytics?.performance.uptime || 0) > 0.99 ? 'Excellent' : 'Good'}
          </div>
        </div>
      </Card>
      
      <Card className="p-6 glass shadow-glass">
        <div className="text-center">
          <div className="text-3xl font-bold text-momentum-amber font-inter mb-2">
            {formatPercentage(analytics?.performance.errorRate || 0)}
          </div>
          <div className="text-white/70 font-inter">Error Rate</div>
          <div className={`text-sm font-inter mt-2 ${
            (analytics?.performance.errorRate || 0) < 0.01 ? 'text-momentum-emerald' : 'text-momentum-amber'
          }`}>
            {(analytics?.performance.errorRate || 0) < 0.01 ? 'Excellent' : 'Monitor'}
          </div>
        </div>
      </Card>
      
      <Card className="p-6 glass shadow-glass">
        <div className="text-center">
          <div className="text-3xl font-bold text-momentum-purple font-inter mb-2">
            {formatPercentage(analytics?.performance.cacheHitRate || 0)}
          </div>
          <div className="text-white/70 font-inter">Cache Hit Rate</div>
          <div className="text-momentum-emerald text-sm font-inter mt-2">
            {(analytics?.performance.cacheHitRate || 0) > 0.8 ? 'Excellent' : 'Good'}
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background-base p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-text-primary font-inter mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-text-secondary font-inter">
            Monitor your momentum, interventions, and system performance
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <Button
                key={tab.key}
                onClick={() => setSelectedView(tab.key as any)}
                variant={selectedView === tab.key ? "primary" : "ghost"}
                size="md"
                ambient={true}
                className="font-inter"
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </Button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedView}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {selectedView === 'overview' && renderOverview()}
            {selectedView === 'interventions' && renderInterventions()}
            {selectedView === 'checkins' && renderCheckIns()}
            {selectedView === 'engagement' && renderEngagement()}
            {selectedView === 'performance' && renderPerformance()}
          </motion.div>
        </AnimatePresence>
      </div>
      </div>
    </ErrorBoundary>
  );
};

export default AnalyticsDashboard;